import React, { useEffect, useState } from "react";
import Menu from "../panel/Menu";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  Avatar,
  TextField,
  Pagination,
  Stack,
  CircularProgress,
  IconButton,
  Chip
} from "@mui/material";
import {
  DeleteOutline,
  CloudUploadOutlined,
  Search,
  Close
} from "@mui/icons-material";
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db, storage } from "../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import Swal from "sweetalert2";

function Propimages() {
  const auth = getAuth();

  const [userId, setUserId] = useState(null);
  const [propertyId, setPropertyId] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [images, setImages] = useState([]);
  const [roomId, setRoomId] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const rowsPerPage = 5;

  /* ================= AUTH ================= */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) return;
      setUserId(user.uid);

      const snap = await getDocs(collection(db, "authroles"));
      snap.forEach((d) => {
        if (d.id === user.uid) {
          setPropertyId(d.data().propertyId);
        }
      });
    });

    return () => unsub();
  }, []);

  /* ================= FETCH ROOMS ================= */
  const fetchRooms = async () => {
    if (!userId || !propertyId) return;

    const snap = await getDocs(collection(db, "rooms"));
    setRooms(
      snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((r) => r.propertyId === propertyId)
    );
  };

  /* ================= FETCH IMAGES ================= */
  const fetchImages = async () => {
    if (!userId || !propertyId) return;

    const snap = await getDocs(collection(db, "property_images"));
    setImages(
      snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((img) => img.propertyId === propertyId)
    );
  };

  useEffect(() => {
    fetchRooms();
    fetchImages();
  }, [userId, propertyId]);

  /* ================= FILE SELECTION ================= */
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles((prev) => [...prev, ...selectedFiles]);
    // Reset input value to allow selecting the same file again if needed
    e.target.value = null;
  };

  const removeSelectedFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  /* ================= UPLOAD ================= */
  const uploadImages = async () => {
    if (!files.length) return;

    setUploading(true);
    try {
      await Promise.all(
        files.map(async (file) => {
          const folder = roomId || "property";
          const storageRef = ref(
            storage,
            `property_images/${propertyId}/${folder}/${Date.now()}_${file.name}`
          );

          await uploadBytes(storageRef, file);
          const url = await getDownloadURL(storageRef);

          await addDoc(collection(db, "property_images"), {
            userId,
            propertyId,
            roomId: roomId || null,
            imageUrl: url,
            createdAt: new Date()
          });
        })
      );

      Swal.fire("Success", "Images uploaded successfully", "success");
      setFiles([]);
      setRoomId("");
      fetchImages();
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    } finally {
      setUploading(false);
    }
  };

  /* ================= DELETE ================= */
  const deleteImage = async (id) => {
    await deleteDoc(doc(db, "property_images", id));
    Swal.fire("Deleted", "Image removed", "success");
    fetchImages();
  };

  /* ================= FILTER ================= */
  const propertyImages = images.filter((img) => !img.roomId);
  const roomImages = images.filter((img) => img.roomId);

  const filteredRoomImages = roomImages.filter((img) =>
    rooms
      .find((r) => r.id === img.roomId)
      ?.roomTypeName?.toLowerCase()
      .includes(search.toLowerCase())
  );

  const paginated = filteredRoomImages.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Box display="flex" minHeight="100vh" bgcolor="#f4f6f8">
      <Menu />

      <Box flex={1} p={4} className="content-wrapper">
        <Typography fontSize={26} fontWeight={800} mb={3}>
          Property & Room Images
        </Typography>

        {/* ================= UPLOAD ================= */}
        <Card sx={{ borderRadius: 4, mb: 4 }}>
          <CardContent>
            <Typography fontWeight={700} mb={2}>
              Upload Images
            </Typography>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <Select
                  fullWidth
                  size="small"
                  value={roomId}
                  onChange={(e) => setRoomId(e.target.value)}
                  displayEmpty
                  disabled={uploading}
                >
                  <MenuItem value="">Property Images</MenuItem>
                  {rooms.map((r) => (
                    <MenuItem key={r.id} value={r.id}>
                      {r.roomTypeName}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              <Grid item xs={12} md={8}>
                <input
                  type="file"
                  multiple
                  hidden
                  id="uploadInput"
                  accept="image/*"
                  onChange={handleFileChange}
                />

                <Button
                  variant="outlined"
                  startIcon={<CloudUploadOutlined />}
                  onClick={() =>
                    document.getElementById("uploadInput").click()
                  }
                  disabled={uploading}
                >
                  Select Images
                </Button>

                <Button
                  sx={{ ml: 2, minWidth: 120 }}
                  variant="contained"
                  disabled={!files.length || uploading}
                  onClick={uploadImages}
                  startIcon={
                    uploading ? <CircularProgress size={20} color="inherit" /> : null
                  }
                >
                  {uploading ? "Uploading..." : "Upload"}
                </Button>
              </Grid>
            </Grid>

            {/* ================= SELECTED FILES PREVIEW ================= */}
            {files.length > 0 && (
              <Box mt={3}>
                <Typography variant="subtitle2" fontWeight={600} mb={1}>
                  Selected Images ({files.length}):
                </Typography>
                <Grid container spacing={2}>
                  {files.map((file, index) => {
                    const previewUrl = URL.createObjectURL(file);
                    return (
                      <Grid item key={index}>
                        <Box
                          sx={{
                            position: "relative",
                            width: 100,
                            height: 80,
                            borderRadius: 2,
                            overflow: "hidden",
                            border: "1px solid #e0e0e0"
                          }}
                        >
                          <Avatar
                            variant="rounded"
                            src={previewUrl}
                            sx={{ width: "100%", height: "100%" }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => removeSelectedFile(index)}
                            disabled={uploading}
                            sx={{
                              position: "absolute",
                              top: 2,
                              right: 2,
                              bgcolor: "rgba(0, 0, 0, 0.6)",
                              color: "#fff",
                              p: "2px",
                              "&:hover": { bgcolor: "rgba(0, 0, 0, 0.8)" }
                            }}
                          >
                            <Close sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                        <Typography
                          variant="caption"
                          noWrap
                          sx={{ display: "block", width: 100, mt: 0.5 }}
                        >
                          {file.name}
                        </Typography>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* ================= PROPERTY IMAGES ================= */}
        <Card sx={{ borderRadius: 4, mb: 4 }}>
          <CardContent>
            <Typography fontWeight={700} mb={2}>
              Property Images
            </Typography>

            <Grid container spacing={2}>
              {propertyImages.map((img) => (
                <Grid item key={img.id}>
                  <Avatar
                    variant="rounded"
                    src={img.imageUrl}
                    sx={{ width: 160, height: 100 }}
                  />
                  <Button
                    color="error"
                    size="small"
                    startIcon={<DeleteOutline />}
                    onClick={() => deleteImage(img.id)}
                  >
                    Delete
                  </Button>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        {/* ================= ROOM TYPE IMAGES ================= */}
        <Card sx={{ borderRadius: 4 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" mb={2}>
              <Typography fontWeight={700}>
                Room Type Images
              </Typography>

              <TextField
                size="small"
                placeholder="Search room"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1 }} />
                }}
              />
            </Stack>

            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Image</TableCell>
                    <TableCell>Room Type</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {paginated.map((img) => (
                    <TableRow key={img.id}>
                      <TableCell>
                        <Avatar
                          variant="rounded"
                          src={img.imageUrl}
                          sx={{ width: 90, height: 60 }}
                        />
                      </TableCell>

                      <TableCell>
                        {rooms.find((r) => r.id === img.roomId)?.roomTypeName}
                      </TableCell>

                      <TableCell align="right">
                        <Button
                          color="error"
                          startIcon={<DeleteOutline />}
                          onClick={() => deleteImage(img.id)}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Pagination
                count={Math.ceil(filteredRoomImages.length / rowsPerPage) || 1}
                page={page}
                onChange={(e, v) => setPage(v)}
              />
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default Propimages;